import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

// Down Payment Calculator — two sub-calculators as per image
export default function DownPaymentCalc() {
  return (
    <CalcShell look="cnet" title="Down Payment Calculator" subtitle="Calculate home price you can afford from upfront cash, or figure out how much cash you'll need for a target home price."
      about={<>
        <p>The <b>down payment</b> is the portion of a home's price paid upfront. A larger down payment reduces the loan amount, monthly payment, total interest, and may eliminate PMI.</p>
      </>}
    >
      <UseUpfront/>
      <hr className="my-8 border-slate-200" />
      <UseHomePrice/>
      <hr className="my-8 border-slate-200" />
      <UsePriceAndCash/>
      <hr className="my-8 border-slate-200" />
      <HowLongToSave/>
    </CalcShell>
  );
}

/* Sub-calc: Use the Home Price and Upfront Cash Available (matches Calculator.net reference) */
function UsePriceAndCash() {
  const [price, setPrice] = useState(500000);
  const [cash, setCash] = useState(100000);
  const [includeCC, setIncludeCC] = useState(true);
  const [ccPct, setCcPct] = useState(3);
  const [ccMode, setCcMode] = useState('%');
  const [rate, setRate] = useState(6.625);
  const [term, setTerm] = useState(30);

  const c = useMemo(() => {
    const ccAmt = includeCC ? (ccMode === '%' ? price * ccPct/100 : ccPct) : 0;
    const down = Math.max(0, cash - ccAmt);
    const downPct = price > 0 ? down / price * 100 : 0;
    const loan = Math.max(0, price - down);
    const r = rate/100/12;
    const n = term * 12;
    const monthly = r === 0 ? loan / n : (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return { down, downPct, ccAmt, loan, monthly };
  }, [price, cash, includeCC, ccPct, ccMode, rate, term]);

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Use the Home Price and Upfront Cash Available</h3>
      <p className="text-sm text-slate-600 mb-4">If the home price and amount of upfront cash available are known, use the calculator below to calculate an estimate for a down payment percentage.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Home Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={price} onChange={e=>setPrice(+e.target.value||0)} className="flex-1" data-testid="dp2-price"/></div></div>
          <div className="field"><label>Upfront Cash Available</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={cash} onChange={e=>setCash(+e.target.value||0)} className="flex-1" data-testid="dp2-cash"/></div></div>
          <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={includeCC} onChange={e=>setIncludeCC(e.target.checked)}/> Include Closing Costs</label>
          {includeCC && (
            <div className="field"><label>Closing Costs</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step="0.01" value={ccPct} onChange={e=>setCcPct(+e.target.value||0)}/>
                <select value={ccMode} onChange={e=>setCcMode(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
              </div>
            </div>
          )}
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Down Payment</div><div className="result-value" data-testid="dp2-downpct">{c.downPct.toFixed(1)}%</div></div>
          <table className="data-table"><tbody>
            <tr><td>Down Payment</td><td className="text-right font-semibold">{fmt(c.down)}</td></tr>
            <tr><td>Down Payment Percentage</td><td className="text-right font-semibold">{c.downPct.toFixed(1)}%</td></tr>
            <tr><td>Closing Costs</td><td className="text-right">{fmt(c.ccAmt)}</td></tr>
            <tr><td>Loan Amount</td><td className="text-right">{fmt(c.loan)}</td></tr>
            <tr><td>Monthly Payment</td><td className="text-right font-semibold text-blue-700">{fmt(c.monthly)}</td></tr>
          </tbody></table>
          {c.downPct < 20 && <p className="text-xs text-slate-600 mt-3">Since the down payment is less than 20%, most probably you will be asked to pay PMI Insurance or mortgage insurance premium.</p>}
        </div>
      </div>
    </div>
  );
}

/* Sub-calc 3: How long will it take to save for a down payment? */
function HowLongToSave() {
  const [price, setPrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [saved, setSaved] = useState(20000);
  const [monthlySave, setMonthlySave] = useState(1500);
  const [returnRate, setReturnRate] = useState(4);

  const c = useMemo(() => {
    const target = price * downPct / 100;
    const remaining = Math.max(0, target - saved);
    const r = returnRate / 100 / 12;
    // Solve n from FV = S(1+r)^n + M((1+r)^n - 1)/r = target
    // (S + M/r)(1+r)^n - M/r = target
    // (1+r)^n = (target + M/r) / (S + M/r)
    let months = Infinity;
    if (monthlySave > 0 || saved >= target) {
      if (r === 0) {
        months = monthlySave > 0 ? remaining / monthlySave : (remaining === 0 ? 0 : Infinity);
      } else {
        const A = target + monthlySave / r;
        const B = saved + monthlySave / r;
        if (B > 0 && A / B > 0) {
          months = Math.log(A / B) / Math.log(1 + r);
          if (!isFinite(months) || months < 0) months = 0;
        }
      }
    }
    const years = months / 12;
    return { target, remaining, months, years };
  }, [price, downPct, saved, monthlySave, returnRate]);

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">How Long Will It Take to Save the Down Payment?</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Home Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={price} onChange={e=>setPrice(+e.target.value||0)} className="flex-1" data-testid="dp3-price"/></div></div>
          <div className="field"><label>Down Payment</label><div className="flex gap-2"><input type="number" step="0.01" value={downPct} onChange={e=>setDownPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Amount Already Saved</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={saved} onChange={e=>setSaved(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Additional Monthly Savings</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthlySave} onChange={e=>setMonthlySave(+e.target.value||0)} className="flex-1" data-testid="dp3-monthly"/></div></div>
          <div className="field"><label>Return on Savings</label><div className="flex gap-2"><input type="number" step="0.01" value={returnRate} onChange={e=>setReturnRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3">
            <div className="result-title">Time to Reach Down Payment Goal</div>
            <div className="result-value" data-testid="dp3-time">
              {isFinite(c.months) ? `${Math.floor(c.years)} yrs ${Math.round(c.months % 12)} mo` : 'Never — increase monthly savings'}
            </div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Down Payment Target</td><td className="text-right font-semibold">{fmt(c.target)}</td></tr>
            <tr><td>Already Saved</td><td className="text-right">{fmt(saved)}</td></tr>
            <tr><td>Remaining to Save</td><td className="text-right text-orange-600 font-semibold">{fmt(c.remaining)}</td></tr>
            <tr><td>Monthly Savings</td><td className="text-right">{fmt(monthlySave)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

function UseUpfront() {
  const [cash, setCash] = useState(100000);
  const [downPct, setDownPct] = useState(20);
  const [includeClosing, setIncludeClosing] = useState(true);
  const [closingPct, setClosingPct] = useState(3);
  const [rate, setRate] = useState(6.625);
  const [term, setTerm] = useState(30);
  const c = useMemo(() => {
    const closingFactor = includeClosing ? closingPct/100 : 0;
    // cash = homePrice * (downPct/100 + closingFactor)
    const homePrice = cash / (downPct/100 + closingFactor);
    const down = homePrice * downPct/100;
    const closing = homePrice * closingFactor;
    const loan = homePrice - down;
    const r = rate/100/12, n = term*12;
    const M = r === 0 ? loan/n : (loan*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    return { homePrice, down, closing, loan, M };
  }, [cash, downPct, includeClosing, closingPct, rate, term]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Use the Upfront Cash Available</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Upfront Cash Available</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={cash} onChange={e=>setCash(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Down Payment</label><div className="flex gap-2"><input type="number" step="0.01" value={downPct} onChange={e=>setDownPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeClosing} onChange={e=>setIncludeClosing(e.target.checked)}/> Include Closing Costs
            <input type="number" step="0.01" value={closingPct} onChange={e=>setClosingPct(+e.target.value||0)} className="!py-1 !w-16 ml-2" disabled={!includeClosing}/><span className="text-slate-500">%</span>
          </label>
          <div className="field mt-3"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
        </div>
        <div>
          <table className="data-table">
            <tbody>
              <tr><td>Home Price</td><td className="text-right font-semibold text-lg text-blue-700">{fmt(c.homePrice)}</td></tr>
              <tr><td>Down Payment</td><td className="text-right">{fmt(c.down)}</td></tr>
              <tr><td>Closing Costs</td><td className="text-right">{fmt(c.closing)}</td></tr>
              <tr><td>Loan Amount</td><td className="text-right font-semibold">{fmt(c.loan)}</td></tr>
              <tr><td>Monthly Payment</td><td className="text-right font-semibold text-blue-700">{fmt(c.M)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UseHomePrice() {
  const [price, setPrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [includeClosing, setIncludeClosing] = useState(true);
  const [closingPct, setClosingPct] = useState(3);
  const [rate, setRate] = useState(6.625);
  const [term, setTerm] = useState(30);
  const c = useMemo(() => {
    const down = price * downPct/100;
    const closing = includeClosing ? price * closingPct/100 : 0;
    const cashNeeded = down + closing;
    const loan = price - down;
    const r = rate/100/12, n = term*12;
    const M = r === 0 ? loan/n : (loan*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    return { down, closing, cashNeeded, loan, M };
  }, [price, downPct, includeClosing, closingPct, rate, term]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Use the Home Price</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Home Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={price} onChange={e=>setPrice(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Down Payment</label><div className="flex gap-2"><input type="number" step="0.01" value={downPct} onChange={e=>setDownPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeClosing} onChange={e=>setIncludeClosing(e.target.checked)}/> Include Closing Costs
            <input type="number" step="0.01" value={closingPct} onChange={e=>setClosingPct(+e.target.value||0)} className="!py-1 !w-16 ml-2" disabled={!includeClosing}/><span className="text-slate-500">%</span>
          </label>
          <div className="field mt-3"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Cash Needed</div><div className="result-value">{fmt(c.cashNeeded)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Down Payment</td><td className="text-right">{fmt(c.down)}</td></tr>
            <tr><td>Closing Costs</td><td className="text-right">{fmt(c.closing)}</td></tr>
            <tr><td>Down Payment + Closing Costs</td><td className="text-right font-semibold">{fmt(c.cashNeeded)}</td></tr>
            <tr><td>Loan Amount</td><td className="text-right">{fmt(c.loan)}</td></tr>
            <tr><td>Monthly Payment</td><td className="text-right font-semibold text-blue-700">{fmt(c.M)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}
